import { Tooltip } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { Button, Trans } from '@/components'
import { EmptyView } from '@/components/EmptyView.tsx'
import { AVAILABLE_CUSTOM_IMAGE_STATUSES, isImageLaunchUnavailable } from '@/consts/image'
import { GhostType, MachineType } from '@/consts/machine'
import { Image } from '@/models/image'
import { Machine } from '@/models/machine'
import {
  buildBaseResourceDesc,
  getBootSourceCompatibility,
  getMinimumInstanceCountForBootSource,
} from '@/pages/create-instance/utils/extraStorage'
import { fetchAllPages } from '@/utils/pagination'
import { cn } from '@/utils/style'

import { useSelectionScope } from '../hooks/useSelectionScope'
import { ImageAttributeSelection, serializeImageAttributeFilters } from '../utils/imageFilters'
import { BOOT_SOURCE_PAGE_SIZE, BootSourcePagination } from './boot-source-pagination'
import { BootSourceSkeleton } from './boot-source-skeleton'
import { BootSourceCompatibilityMessage } from './BootSourceCompatibilityMessage'
import { ImageAttributeFilters } from './ImageAttributeFilters'
import { MINIMUM_RESOURCE_TOOLTIP_PROPS, MinimumResourceBadge } from './minimum-resource-badge'
import { SelectMachineWarning } from './select-machine-warning'

interface ImageSelectorProps {
  machine?: Machine
  type: MachineType
  value?: Image
  count?: number
  onChange: (value?: Image) => void
  onImagesChange?: (value: Image[]) => void
  ghostType?: GhostType
  emptyMessageVariant?: 'default' | 'team'
  enforceResourceLimit?: boolean
  supportsExtraResources?: boolean
  searchTerm?: string
  keywords?: string[]
  paginateOnServer?: boolean
  filters?: ImageAttributeSelection
}

export function ImageSelector({
  value,
  machine,
  count,
  onChange,
  onImagesChange,
  ghostType,
  emptyMessageVariant = 'default',
  enforceResourceLimit = false,
  supportsExtraResources = false,
  searchTerm = '',
  keywords = [],
  paginateOnServer = false,
  filters,
}: ImageSelectorProps) {
  const scope = useSelectionScope()
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [showSkeleton, setShowSkeleton] = useState(false)
  const [images, setImages] = useState<Image[]>([])
  const [pagination, setPagination] = useState({ contextKey: '', page: 1 })
  const [total, setTotal] = useState(0)
  const [attributeSelection, setAttributeSelection] = useState<ImageAttributeSelection>({})
  const attrFilters = serializeImageAttributeFilters(filters ?? attributeSelection)
  const prevMachineKeyRef = useRef<string | undefined>(machine?.key)
  const prevGhostTypeRef = useRef<GhostType | undefined>()
  const loadedContextKeyRef = useRef<string>()
  const requestIdRef = useRef(0)
  const { t } = useTranslation()
  const isSubscriptionResourceMode = enforceResourceLimit && !supportsExtraResources
  const keywordsParam = paginateOnServer && keywords.length ? JSON.stringify(keywords) : undefined
  const imageContextKey = `${scope}:${machine?.key ?? ''}:${count}:${ghostType ?? ''}:${attrFilters ?? ''}:${keywordsParam ?? ''}`
  useEffect(() => {
    setPagination({ contextKey: imageContextKey, page: 1 })
  }, [imageContextKey])
  const page = pagination.contextKey === imageContextKey ? pagination.page : 1
  const setPage = (page: number) => setPagination({ contextKey: imageContextKey, page })
  const requestContextKey = `${imageContextKey}:${paginateOnServer ? page : 'all'}`
  const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase()
  const getImages = useCallback(async () => {
    const requestId = ++requestIdRef.current
    const isInitialContextLoad = loadedContextKeyRef.current !== requestContextKey

    setLoading(true)
    setLoadError(false)
    setShowSkeleton(isInitialContextLoad)
    if (!machine) {
      setLoading(false)
      setShowSkeleton(false)
      return
    }

    try {
      const fetchPage = (page: number, pageSize: number) =>
        API.getImageList({
          agent_id: machine.agent_id ?? 0,
          machine_category: machine.machineCategory,
          resource_desc: buildBaseResourceDesc(machine, count ?? 1),
          page,
          per_page: pageSize,
          attr_filters: attrFilters,
          keywords: keywordsParam,
          owned: (paginateOnServer && ghostType === GhostType.CustomImage) || undefined,
        })
      // Legacy consumers can still opt into a complete list; creation uses server pagination.
      const response = paginateOnServer ? await fetchPage(page, BOOT_SOURCE_PAGE_SIZE) : undefined
      if (response && response.code !== 0) {
        throw new Error(response.msg || 'Failed to fetch image page')
      }
      const serverImages = response
        ? response.images
        : await fetchAllPages({ fetchPage, getItems: (response) => response.images })

      if (requestId !== requestIdRef.current) return

      let filteredImages = serverImages

      // Keep legacy consumers unchanged; server-paginated lists display every returned image.
      if (!paginateOnServer && ghostType === GhostType.Image) {
        // Official images (public = true)
        filteredImages = filteredImages.filter((im) => im.public === true)
      } else if (!paginateOnServer && ghostType === GhostType.CustomImage) {
        // Custom images (public = false and available status)
        filteredImages = filteredImages.filter(
          (im) => !im.public && AVAILABLE_CUSTOM_IMAGE_STATUSES.includes(im.storeStatus),
        )
      }

      const nextImages = filteredImages.map((im) => new Image(im))
      setImages(nextImages)
      setTotal(response?.pagination.total ?? nextImages.length)
      onImagesChange?.(nextImages)
      loadedContextKeyRef.current = requestContextKey
    } catch (error) {
      if (requestId !== requestIdRef.current) return
      console.error('Failed to fetch images:', error)
      setLoadError(true)
      setImages([])
      setTotal(0)
      onChange(undefined)
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false)
        setShowSkeleton(false)
      }
    }
  }, [
    machine,
    count,
    ghostType,
    attrFilters,
    keywordsParam,
    onImagesChange,
    requestContextKey,
    paginateOnServer,
  ])

  const filteredImages = useMemo(() => {
    if (paginateOnServer || !normalizedSearchTerm) return images

    return images.filter((image) =>
      [image.id, image.resID, image.alias, image.description].some((value) =>
        String(value).toLocaleLowerCase().includes(normalizedSearchTerm),
      ),
    )
  }, [images, normalizedSearchTerm, paginateOnServer])

  const visibleImages = useMemo(() => {
    if (paginateOnServer) return filteredImages
    const start = (page - 1) * BOOT_SOURCE_PAGE_SIZE
    return filteredImages.slice(start, start + BOOT_SOURCE_PAGE_SIZE)
  }, [filteredImages, page, paginateOnServer])

  useEffect(() => {
    if (!paginateOnServer) setPage(1)
  }, [normalizedSearchTerm, paginateOnServer])

  useEffect(() => {
    if (paginateOnServer || normalizedSearchTerm) return

    const selectedIndex = images.findIndex((image) => image.id === value?.id)
    if (selectedIndex >= 0) {
      setPage(Math.floor(selectedIndex / BOOT_SOURCE_PAGE_SIZE) + 1)
    }
  }, [images, normalizedSearchTerm, value?.id, paginateOnServer])

  useEffect(() => {
    if (machine) {
      getImages()

      // Clear selection when machine or ghostType changes
      const machineChanged = prevMachineKeyRef.current !== machine.key
      const ghostTypeChanged =
        prevGhostTypeRef.current !== undefined && prevGhostTypeRef.current !== ghostType

      if ((prevMachineKeyRef.current !== undefined && machineChanged) || ghostTypeChanged) {
        onChange(undefined)
      }
    }

    prevMachineKeyRef.current = machine?.key
    prevGhostTypeRef.current = ghostType
    return () => {
      requestIdRef.current += 1
    }
  }, [machine, ghostType, getImages])

  useEffect(() => {
    if (
      !loading &&
      loadedContextKeyRef.current === requestContextKey &&
      value &&
      !images.some(
        (image) =>
          image.resID === value.resID && (!paginateOnServer || !isImageLaunchUnavailable(image)),
      )
    )
      onChange(undefined)
  }, [images, loading, requestContextKey, value, onChange, paginateOnServer])

  if (!machine) {
    return <SelectMachineWarning />
  }

  return (
    <section
      className="bg-panel-white rounded-2xl"
      aria-busy={loading}
      data-ai-component="image-selector"
      data-ai-state={value ? 'selected' : 'needs-selection'}
      data-ai-value={value?.resID ?? ''}
    >
      {filters === undefined && (
        <div className="mb-4">
          <ImageAttributeFilters
            value={attributeSelection}
            onChange={(next) => {
              setAttributeSelection(next)
              onChange(undefined)
            }}
          />
        </div>
      )}
      {paginateOnServer && (
        <p className="mb-4 t-xs text-tertiary">{t('create.imageView.keywordSearchHint')}</p>
      )}
      {showSkeleton && <BootSourceSkeleton />}
      {!showSkeleton && filteredImages.length > 0 && (
        <div
          className="grid h-[244px] grid-cols-4 grid-rows-2 gap-4 overflow-hidden"
          role="radiogroup"
          aria-label={t('create.bootSourceType.image')}
        >
          {visibleImages.map((image) => {
            const minimumInstanceCount = getMinimumInstanceCountForBootSource({
              machine,
              count,
              source: image,
              supportsExtraResources,
            })
            const resourceDisabled = enforceResourceLimit && minimumInstanceCount === undefined
            const statusDisabled = paginateOnServer && isImageLaunchUnavailable(image)
            const disabled = resourceDisabled || statusDisabled
            const compatibility = getBootSourceCompatibility({
              machine,
              count: resourceDisabled ? machine.maxAvailUnits : count,
              source: image,
              supportsExtraResources,
            })
            const disabledReason = statusDisabled ? (
              t('create.imageView.unavailableStatus')
            ) : resourceDisabled ? (
              <BootSourceCompatibilityMessage
                compatibility={compatibility}
                sourceType="image"
                supportsExtraResources={supportsExtraResources}
              />
            ) : undefined
            const selected = !disabled && value?.id === image.id

            return (
              <Tooltip
                key={image.id}
                title={disabledReason}
                trigger={['hover', 'focus']}
                {...(resourceDisabled && !statusDisabled && isSubscriptionResourceMode
                  ? MINIMUM_RESOURCE_TOOLTIP_PROPS
                  : {})}
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-disabled={disabled}
                  className={cn(
                    'h-full w-full overflow-hidden rounded-lg border border-neutral-200 p-4 text-left cursor-pointer',
                    {
                      'bg-highlight/10 border-secondary': selected,
                      'opacity-50 cursor-not-allowed': disabled,
                    },
                  )}
                  data-ai-component="image-option"
                  data-ai-action="select-image"
                  data-ai-state={disabled ? 'disabled' : selected ? 'selected' : 'available'}
                  data-ai-value={image.resID}
                  aria-label={`${image.resID} ${image.alias}`}
                  onClick={(event) => {
                    event.stopPropagation()
                    if (!disabled) onChange(image)
                  }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="min-w-0 flex-1 truncate t-xs text-primary">{image.resID}</div>
                    <MinimumResourceBadge resource={compatibility.required} />
                  </div>
                  <div className="t-sb mb-0.5 truncate text-primary">{image.alias}</div>
                  <p className="line-clamp-2 text-secondary t-xs">
                    {statusDisabled ? t('create.imageView.unavailableStatus') : image.description}
                  </p>
                </button>
              </Tooltip>
            )
          })}
        </div>
      )}

      {!showSkeleton && (paginateOnServer ? total > 0 : filteredImages.length > 0) && (
        <BootSourcePagination
          current={page}
          selected={value && filteredImages.some((image) => image.id === value.id) ? 1 : 0}
          total={paginateOnServer ? total : filteredImages.length}
          serverPaginated={paginateOnServer}
          onChange={(page) => {
            if (paginateOnServer) onChange(undefined)
            setPage(page)
          }}
        />
      )}

      {!showSkeleton && loadError && (
        <div role="alert" className="p-8 text-center">
          <p className="mb-4 t-sm">{t('create.imageView.loadFailed')}</p>
          <Button type="outline" onClick={() => void getImages()}>
            {t('create.imageView.retry')}
          </Button>
        </div>
      )}
      {!showSkeleton && !loadError && filteredImages.length === 0 && (
        <EmptyView
          className="h-[300px]"
          content={
            <span className="t-sm text-secondary">
              {paginateOnServer ? (
                t('create.imageView.noImages')
              ) : normalizedSearchTerm && images.length > 0 ? (
                <Trans i18nKey="create.bootSourceSearchNoResults">No matching boot sources</Trans>
              ) : emptyMessageVariant === 'team' ? (
                <span className="block max-w-[760px] text-center leading-6">
                  <Trans i18nKey="customImage.filter.teamNoCompatibleImages">
                    No compatible images are currently available for this machine. Please contact
                    your administrator to configure the required compatible image.
                  </Trans>
                </span>
              ) : (
                <Trans i18nKey="common.emptyViewNoDataMsg">No Data</Trans>
              )}
            </span>
          }
        />
      )}
    </section>
  )
}
